import './assets/main.css'

import { createApp } from 'vue'
import App from '@/App.vue'
import router from '@/router/index.js'
import storage from '@/plugins/storage'
import appAxios from '@/plugins/axios'
import utils from '@/plugins/utils'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'
import PrimeVue from 'primevue/config'
import { captureError } from '@/utils/sentry'

const pinia = createPinia()
const app = createApp(App)
/**
 * Plugin install
 */
app.use(router)
app.use(pinia)
app.use(appAxios, {
  baseUrl: import.meta.env.VITE_APP_API_URL
})
app.use(PrimeVue)
/**
 * Provides install
 */
app.provide('storage', storage)
app.provide('utils', utils)

Sentry.init({
  app,
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.VITE_APP_ENV,
  integrations: [
    new Sentry.BrowserTracing({
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        'localhost',
        import.meta.env.VITE_SENTRY_DOMAIN,
        import.meta.env.VITE_APP_PRODUCTION_URL
      ],
      routingInstrumentation: Sentry.vueRouterInstrumentation(router)
    }),
    new Sentry.Replay()
  ],
  // Performance Monitoring
  tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? 1.0),
  replaysSessionSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE ?? 0.1),
  replaysOnErrorSampleRate: Number(import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE ?? 1.0)
})

// Google Maps calls this global callback asynchronously when the API key is invalid,
// restricted to the wrong domain, or billing isn't enabled — the raw <script> tag in
// index.html still loads with a 200 in that case, so this failure is otherwise invisible.
window.gm_authFailure = () => {
  captureError(new Error('Google Maps authentication failure (invalid/restricted API key)'), {
    tags: { integration: 'google_maps' }
  })
  console.error('[Google Maps] Authentication failure — check API key restrictions/billing.')
}

// The Google Maps <script> tag in index.html is render-blocking (no async/defer), so it
// either loads or fails before this module even starts running — an event listener
// registered here would always be too late. index.html's inline onerror sets this flag
// instead, checked once we're actually able to report it.
if (window.__googleMapsScriptFailed) {
  captureError(new Error('Google Maps script failed to load'), {
    tags: { integration: 'google_maps' }
  })
}

app.mount('#app')
